import { useEffect, useMemo, useState } from 'react';

import { startOfToday } from 'date-fns';
import { useDispatch, useSelector } from 'hooks';
import { sortBy, uniqBy } from 'lodash';

import { Interval, PlanGroup, PlanUnit } from 'components/types';

import { PlanUnitType } from 'components/enums';

import { sortAlphabeticallyBy } from 'utils/misc';

import { isValidPlanUnit } from 'components/shared/PlanUnitSelector/util';

import { fetchPlanUnits } from '../redux/actionCreators';

type UseOptionsProps = {
  showGroups: boolean;
  useEmployeePlanunits?: boolean;
  usePublicPlanUnits?: boolean;
  interval?: Interval;
  filterByType?: PlanUnitType;
};

export default (props: UseOptionsProps) => {
  const {
    showGroups = true,
    useEmployeePlanunits,
    usePublicPlanUnits,
    interval: intervalProp,
    filterByType
  } = props;
  const dispatch = useDispatch();
  const planGroups = useSelector((state) => state.planGroups);
  const planUnits = useSelector(
    (state) =>
      state[
        useEmployeePlanunits
          ? 'employeePlanUnits'
          : usePublicPlanUnits
          ? 'publicPlanUnits'
          : 'planUnits'
      ]
  );
  const [options, setOptions] = useState<(PlanUnit | PlanGroup)[]>([]);
  const interval = useMemo(
    () =>
      intervalProp || { startDate: startOfToday(), endDate: startOfToday() },
    [intervalProp]
  );

  useEffect(() => {
    dispatch(
      fetchPlanUnits({
        interval,
        useEmployeePlanunits,
        usePublicPlanUnits,
        filterByType
      })
    );
    if (showGroups) {
      dispatch({ type: 'FETCH_PLANGROUPS' });
    }
  }, [
    dispatch,
    showGroups,
    useEmployeePlanunits,
    usePublicPlanUnits,
    interval
  ]);

  useEffect(() => {
    const planGroupOptions = showGroups
      ? sortAlphabeticallyBy(planGroups, 'name')
      : [];
    const uniqueValidPlanUnits = uniqBy(
      sortBy(planUnits, (o) => isValidPlanUnit(o.validity, interval)).reverse(),
      'id'
    );

    setOptions([
      ...planGroupOptions,
      ...sortAlphabeticallyBy(uniqueValidPlanUnits, 'name')
    ]);
  }, [showGroups, planGroups, planUnits, interval]);

  return options;
};
